/* eslint-disable no-undef */
import {
  FluentProvider,
  makeStyles,
  shorthands,
  Textarea,
  Button,
  Link,
  Toaster,
  useToastController,
  Toast,
  ToastTitle,
  ToastTrigger,
  ToastBody,
  Spinner,
  mergeClasses,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogActions,
  DialogContent,
  TextareaOnChangeData,
  useId,
} from "@fluentui/react-components";
import { CloseIcon, SendDismissIcon, SendIcon } from "..";
import React, { useEffect, useState, useRef } from "react";
import MyMessageBubble from "./MessageBubble";
import { inject, observer } from "mobx-react";
import { tokens } from "@fluentui/react-theme";
import Header from "./Header";
import IntroCard from "./Intro";
import { Conversation, MessageProps, modelNames } from "./types";
import { documentTheme, gptTheme } from "..";
import Store from "./store";
import { ensureError, extractInitialsFromName, reportError } from "./utilities";
import { SelectDocuments } from "./SelectDocuments";
import Filters from "./Filters";
const baseHostUrl = "https://kafka-legal-analyst-fe-stg.pwc.fi/";
const useStyles = makeStyles({
  root: { width: "100%", height: "100%", display: "flex", flexDirection: "column" },
  chatArea: { height: "100%", overflowY: "auto", display: "flex", flexDirection: "column", whiteSpace: "pre-wrap" },
  blur: { filter: "blur(4px)" },
  bottomPush: { marginTop: "auto", position: "relative" },
  formArea: {
    display: "flex",
    alignItems: "end",
    rowGap: "0.8rem",
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding("0.5rem"),
    backdropFilter: "opacity(0.5)",
  },
  messageInput: { width: "100%", boxSizing: "border-box" },
  sendButton: {
    marginLeft: "0.5rem",
    height: "100%",
    ":disabled": { backgroundColor: tokens.colorBrandBackgroundInvertedPressed },
  },
  listbox: {
    maxHeight: "50px",
    maxWidth: "75px",
  },
  icon: {
    ...shorthands.flex(0, 0, "auto"),
  },
  text: {
    ...shorthands.flex(1, 1, "auto"),
  },
  newButton: {
    ...shorthands.margin("0.2rem", "0.5rem"),
    position: "sticky",
    bottom: "0.1rem",
  },
  card: { width: "100%", position: "absolute", top: "50%", transform: "translate(0, -50%)" },
  modeButton: {
    marginLeft: "1em",
    ...shorthands.border("solid"),
    ...shorthands.borderWidth("2px"),
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    ":hover": {
      ...shorthands.borderColor(tokens.colorBrandStroke2),
    },
  },
  dialogBar: { float: "right" },
  dialogContentWide: {
    width: "700px", // No !important needed
    maxWidth: "90vw", // No !important needed
    zIndex: 99999,
    overFlow: "visible",
  },
  portal: {
    zIndex: 1,
    overFlow: "visible",
  },
  spinner: { width: "100%", position: "absolute", top: "50%", transform: "translate(0, -50%)", zIndex: 1 },
});

const Chat = inject("store")(
  observer(({ store }: { store: Store }) => {
    const [conversation, setConversation] = useState<Conversation>({
      ClientID: store.getClientID(),
      ConversationID: null,
      ModelType: store.getModel(),
      Content: [],
      Summary: "",
      Filters: {
        case_laws: {
          laws: [],
          sections: [],
          keywords: [],
        },
        tax_guidances: {
          tax_guidances: [],
          tax_categories: [],
          keywords: [],
        },
        client_work: {
          document_classes: [],
          related_laws: [],
          keywords: [],
        },
      },
      SelectedDocuments: [],
      TimeStamp: "",
      agent_mode: null,
    });
    const [input, setInput] = useState("");
    const [ws, setWs] = useState(null);
    const token = store.getToken();
    const username = store.getEmail();
    const [connecting, setConnecting] = useState(true);
    const [waiting, setWaiting] = useState(false);
    const [filterOpen, setFilterOpen] = useState(store.getNumFilters() === 0 ? true : false);
    const [showDocuments, setShowDocuments] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const toasterId = useId("toaster");
    const { dispatchToast } = useToastController(toasterId);
    const [url, setUrl] = useState<string | null>(null);

    useEffect(() => {
      if (store.filtersApplied) {
        setShowDocuments(true);
        store.setFiltersApplied(false); // Reset the flag
      }
    }, [store.filtersApplied]);

    const classes = useStyles();

    const overLimit = () => {
      dispatchToast(
        <Toast appearance="inverted">
          <ToastTitle
            action={
              <ToastTrigger>
                <Link>Dismiss</Link>
              </ToastTrigger>
            }
          >
            Character Limit Exceeded
          </ToastTitle>
          <ToastBody>Input accepts only up to 10000 characters</ToastBody>
        </Toast>,
        { intent: "warning", timeout: 2000 }
      );
    };

    const alertUser: (errorMsg: string) => void = (errorMsg: string) => {
      dispatchToast(
        <Toast appearance="inverted">
          <ToastTitle
            action={
              <ToastTrigger>
                <Link>Dismiss</Link>
              </ToastTrigger>
            }
          >
            Oh no!
          </ToastTitle>
          <ToastBody>{errorMsg}</ToastBody>
        </Toast>,
        { intent: "error" }
      );
    };
  
    const informUser: (infoMsg: string) => void = (infoMsg: string) => {
      dispatchToast(
        <Toast>
          <ToastTitle
            action={
              <ToastTrigger>
                <Link>Dismiss</Link>
              </ToastTrigger>
            }
          >
            Info!
          </ToastTitle>
          <ToastBody>{infoMsg}</ToastBody>
        </Toast>,
        { intent: "info" }
      );
    };

    const onChange = (_, newText: TextareaOnChangeData): void => {
      if (newText.value.length > 10000) {
        setInput(newText.value.slice(0, 10000));
        overLimit();
      } else {
        setInput(newText.value);
      }
    };

    function handleClick(_e, val: modelNames) {
      store.setModel(val);
      store.setPrevious(conversation);
      store.setGoback(false);
      store.resetFilters();
      setConversation({
        ClientID: store.getClientID(),
        ConversationID: null,
        ModelType: val,
        Content: [],
        Summary: "",
        Filters: {
          case_laws: {
            laws: [],
            sections: [],
            keywords: [],
          },
          tax_guidances: {
            tax_guidances: [],
            tax_categories: [],
            keywords: [],
          },
          client_work: {
            document_classes: [],
            related_laws: [],
            keywords: [],
          },
        },
        SelectedDocuments: [],
        TimeStamp: "",
        agent_mode: null,
      });
    }

    useEffect(() => {
      try {
        if (store.model === modelNames.baseModelName) {
          setUrl(`wss://kafka-legal-analyst-be-stg.pwc.fi/ws_chat/${username}?access_token=${token}`);
        }
        if (store.model === modelNames.documentModelName) {
          setUrl(`wss://kafka-legal-analyst-be-stg.pwc.fi/ws_chat_documents/${username}?access_token=${token}`);
        }
        if (store.goback) {
          const previous = store.getPrevious();
          setConversation(previous);
          store.setFilters(previous.Filters);
          store.setGoback(false);
        } else {
          setConversation({
            ClientID: store.getClientID(),
            ConversationID: null,
            ModelType: store.model,
            Content: [],
            Summary: "",
            Filters: {
              case_laws: {
                laws: [],
                sections: [],
                keywords: [],
              },
              tax_guidances: {
                tax_guidances: [],
                tax_categories: [],
                keywords: [],
              },
              client_work: {
                document_classes: [],
                related_laws: [],
                keywords: [],
              },
            },
            SelectedDocuments: [],
            TimeStamp: "",
            agent_mode: null,
          });
          store.resetFilters();
        }
      } catch (error: unknown) {
        const causeError = ensureError(error);
        alertUser("Application error. Please try opening the app again.");
        const reportingError = new Error(
          `InModel/GoBack/PreviousConversationSetting.Cause${causeError.message
            .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
            .replace(/\s/g, "")}`
        );
        reportError(reportingError, token);
      }
    }, [store.model, store.bearerToken]);

    useEffect(() => {
      setConnecting(true);
      if (store.bearerToken && store.bearerToken.length > 0 && url) {
        const websocket = new WebSocket(url);

        websocket.onmessage = (event) => {
          try {
            let data = JSON.parse(event.data);
            setWaiting(false);
            let message_received: MessageProps = {
              documentsSelected: store.documentsSelected,
            };
            if (data["error"] !== null && data["error"].length > 0) {
              alertUser(data["error"]);
            } else {
              message_received = data["text"]
                ? {
                    text: data.text,
                    sender: "AI",
                    references: data.references,
                    documentsSelected: store.documentsSelected,
                  }
                : { text: data, sender: "AI", references: data.references, documentsSelected: store.documentsSelected };
              setConversation((prev) => ({
                ...prev,
                ConversationID: data.conversationId,
                Content: [...prev.Content, message_received],
              }));
            }
          } catch (error: unknown) {
            const causeError = ensureError(error);
            alertUser("Error in receiving a message from the bot");
            const reportingError = new Error(
              `InReceivedMessage.Cause${causeError.message
                .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
                .replace(/\s/g, "")}`
            );
            reportError(reportingError, token);
          }
        };

        websocket.onopen = () => {
          setWs(websocket);
          setTimeout(() => setConnecting(false), 950);
        };

        websocket.addEventListener("error", (event) => {
          const causeError = ensureError(event);
          setWaiting(false);
          if (token !== undefined && token.length > 0) {
            alertUser("There is an error connecting to the bot.");
            const reportingError = new Error(
              `WebsocketConnection.Cause${causeError.message
                .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
                .replace(/\s/g, "")}`
            );
            reportError(reportingError, token);
          }
        });

        return () => {
          websocket.close();
        };
      } else {
        return () => {};
      }
    }, [url]);

    useEffect(() => {
      setWaiting(false);
    }, [store.view, store.model]);

    useEffect(() => {
      ref.current.scrollIntoView({ behavior: "smooth" });
      store.setPrevious(conversation);
    }, [conversation.Content]);

    useEffect(() => {
      if (store.getSelectedConversation()) {
        const selected = store.getSelectedConversation();
        setConversation(selected);
        store.setFilters(selected.Filters);
        store.setSelectedConversation(null);
      }
    }, [store.selectedConversation]);

    useEffect(() => {
      {
        conversation?.Content.length === 0 &&
          store.hideIntro &&
          !connecting &&
          setTimeout(
            () =>
              setConversation((conv) => ({
                ...conv,
                Content: [
                  {
                    sender: "AI",
                    text:
                      store.model === "GPT"
                        ? "Hello, you are chatting with GPT!"
                        : store.model === "With docs"
                        ? "Hello, you are chatting with Docs model. With this model you can chat with client work material, court cases and tax administration guidances."
                        : `Hello! You are chatting with ${store.model}!`,
                    references: {},
                    id: "initial",
                    desktop: false,
                    documentsSelected: store.documentsSelected,
                    gptMode: store.getModel() === modelNames.baseModelName,

                    initialItem: true,
                  },
                ],
              })),
            900
          );
      }
    }, [store.hideIntro, connecting, conversation]);

    const sendMessage = (event) => {
      try {
        event.preventDefault();
        if (store.model === modelNames.documentModelName && store.getNumFilters() === 0) {
          alertUser("Please select at least one filter.");
          return;
        }

        if (store.model === modelNames.documentModelName && store.documentsSelected.length === 0) {
          alertUser("Please select at least one document.");
          return;
        }
        setWaiting(true);
        const message_sent = {
          text: input,
          sender: extractInitialsFromName(store.getUsername()),
          conversationId: conversation.ConversationID,
          error: "",
          selected_documents: store.documentsSelected,
          agent_mode: null,
        };
        console.log("Sending message: ", message_sent);

        ws.send(JSON.stringify(message_sent));
        setConversation((conv) => ({
          ...conv,
          Content: [
            ...conv.Content,
            {
              text: input,
              sender: extractInitialsFromName(store.getUsername()),
              conversationId: conversation.ConversationID,
              error: "",
              filters: store.getFilters(), // Ensure this returns PropertiesFilters
              documentsSelected: store.documentsSelected,
              agent_mode: null,
            } as MessageProps,
          ],
        }));
        setInput("");
      } catch (err: unknown) {
        const causeError = ensureError(err);
        alertUser("An error occured sending message to bot.");
        const reportingError = new Error(
          `SendingMessage2BotConversation${conversation.ConversationID ?? ""}.Cause${causeError.message
            .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
            .replace(/\s/g, "")}`
        );
        reportError(reportingError, token);
      }
    };

    return (
      <FluentProvider
        style={{ height: "100%" }}
        theme={
          store.model == modelNames.baseModelName
            ? { ...gptTheme, shadow8Brand: "0 0 2px rgba(0,0,0,0.30), 0 4px 8px rgba(251, 124, 77, 0.3)" }
            : { ...documentTheme, shadow8Brand: "0 0 2px rgba(0,0,0,0.30), 0 4px 8px rgba(41, 84, 119, 0.3)" }
        }
      >
        <main className={classes.root}>
          <Header store={store} />
          <div className={classes.chatArea}>
            {connecting && (
              <Spinner className={classes.spinner} size="huge" label="Connecting" labelPosition="before" />
            )}
            <div className={classes.card}>
              {!store.hideIntro && conversation.Content.length == 0 && !connecting && <IntroCard store={store} />}
            </div>
            <div className={connecting ? mergeClasses(classes.bottomPush, classes.blur) : classes.bottomPush}>
              <MyMessageBubble
                messages={conversation.Content.map((message, index) => ({
                  sender: message.sender,
                  text: message.text,
                  references: message.references,
                  selected_documents: message.documentsSelected ?? [],
                  id: index,
                  desktop: store.getPlatform(),
                  gptMode: store.getModel() === modelNames.baseModelName,
                  initialItem: message.initialItem ? true : false,
                  conversationId: conversation.ConversationID,
                }))}
                inform={informUser}
                error={alertUser}
                userInfo={{ email: store.getEmail(), token: store.getToken() }}
                ref={ref}
              />
              {waiting ? (
                <MyMessageBubble
                  messages={[
                    {
                      sender: "AI",
                      text: "",
                      references: [],
                      id: "wait",
                      loading: true,
                      desktop: false,
                      gptMode: store.getModel() === modelNames.baseModelName,
                    },
                  ]}
                />
              ) : null}
              <div className={classes.newButton}>
                <Menu>
                  <MenuTrigger>
                    <MenuButton size="small" shape="circular" appearance="primary">
                      New +
                    </MenuButton>
                  </MenuTrigger>

                  <MenuPopover>
                    <MenuList>
                      <MenuItem onClick={(e) => handleClick(e, modelNames.documentModelName)} content="With docs">
                        With docs
                      </MenuItem>
                      <MenuItem onClick={(e) => handleClick(e, modelNames.baseModelName)} content="GPT">
                        GPT
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
                {store.getModel() == modelNames.documentModelName && (
                  <Dialog open={filterOpen} onOpenChange={(_event, data) => setFilterOpen(data.open)}>
                    <DialogTrigger disableButtonEnhancement>
                      <Button
                        size="small"
                        shape="circular"
                        appearance={store.getNumFilters() === 0 ? undefined : "primary"}
                        className={classes.modeButton}
                      >
                        Filters
                      </Button>
                    </DialogTrigger>
                    <DialogSurface>
                      <DialogBody>
                        <DialogContent>
                          <div className={classes.dialogBar}>
                            <DialogActions>
                              <DialogTrigger disableButtonEnhancement>
                                <Button icon={<CloseIcon />} appearance="transparent"></Button>
                              </DialogTrigger>
                            </DialogActions>
                          </div>
                          <Filters store={store} handleOpen={() => setFilterOpen(false)} />
                        </DialogContent>
                      </DialogBody>
                    </DialogSurface>
                  </Dialog>
                )}
                {store.getModel() == modelNames.documentModelName && showDocuments && (
                  <Dialog open={true} onOpenChange={(_event, data) => setShowDocuments(data.open)}>
                    <DialogTrigger disableButtonEnhancement>
                      <Button
                        size="small"
                        shape="circular"
                        appearance={Object.values(store.documents).flat().length === 0 ? undefined : "primary"}
                        className={classes.modeButton}
                      >
                        Select 1 - 10 documents
                      </Button>
                    </DialogTrigger>
                    <DialogSurface style={{ minWidth: "600px" }}>
                      <DialogBody>
                        <DialogContent className={classes.dialogContentWide}>
                          <div className={classes.dialogBar}>
                            <DialogActions>
                              <DialogTrigger disableButtonEnhancement>
                                <Button icon={<CloseIcon />} appearance="transparent"></Button>
                              </DialogTrigger>
                            </DialogActions>
                          </div>
                          <SelectDocuments store={store} handleOpen={() => setShowDocuments(false)} />
                        </DialogContent>
                      </DialogBody>
                    </DialogSurface>
                  </Dialog>
                )}
              </div>
            </div>
          </div>
          <form onSubmit={sendMessage} className={classes.formArea}>
            <Textarea
              className={classes.messageInput}
              value={input}
              onChange={onChange}
              placeholder={
                store.model === modelNames.baseModelName
                  ? "Type your message here..."
                  : store.documentsSelected.length > 0
                  ? "Ask your question based on the document(s) selected."
                  : "Select at least one document before typing..."
              }
              autoComplete="off"
              autoFocus={true}
              resize="vertical"
              spellCheck={false}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey && !connecting && !waiting) {
                  event.preventDefault();
                  if (
                    store.model === modelNames.baseModelName ||
                    (store.model === modelNames.documentModelName && store.getNumFilters() > 0)
                  )
                    sendMessage(event);
                }
              }}
            />
            <Button
              type="submit"
              icon={!input || connecting ? <SendDismissIcon /> : <SendIcon />}
              className={classes.sendButton}
              appearance="primary"
              disabled={!input || connecting || waiting}
            />
          </form>
          <Toaster toasterId={toasterId} limit={1} />
        </main>
      </FluentProvider>
    );
  })
);

export default Chat;
